import type { IntlShape } from "react-intl";
import type { Bars, CharacterView, ItemView, LocalizedText } from "@/lib/games/roleplay/contract";

// The examples the tutorial's guide shows, for display only: never the player's own bars,
// inventory or characters and never game state. After the guide the page shows the real ones again. Their words are
// spark's own, in the page's language, so both story languages carry the same text.

const text = (value: string): LocalizedText => ({ de: value, en: value });

// The progress step's bars: a new game's real bars all stand at 0 %, which shows nothing. The example fills whatever 1
// to 5 Powers the game scores, from one cluster or from several (spark#1075), keeping their totals and the mark, so
// some are past the mark and some are not. It lists none of the run's statements (97), so no example bar opens: an
// example has answered nothing. Every Power gets its share, Fidelity too: the assessor reads every turn against every
// statement the game scores, so each of its bars can move (101). A bar whose total is 0 stays at 0 % by itself (23.5).
const EXAMPLE_SHARES = [45, 65, 30, 70, 55];

export function exampleBars(bars: Bars): Bars {
  return {
    ...bars,
    state: "current",
    powers: bars.powers.map((power, index) => ({
      ...power,
      covered: Math.round((power.total * EXAMPLE_SHARES[index % EXAMPLE_SHARES.length]!) / 100),
      statements: [],
    })),
  };
}

// The inventory step's items: one for each state the inventory shows: spoiled food, a device's
// battery, a thing usable once, and a tool that wears out with use and over time, already worn (78).

type Kind = ItemView["category"];
type State = Partial<Pick<ItemView, "usesLeft" | "charge" | "capacity" | "freshness" | "condition">>;

function item(id: string, category: Kind, name: string, description: string, state: State = {}): ItemView {
  return {
    id,
    name: text(name),
    description: text(description),
    category,
    usesLeft: null,
    charge: null,
    capacity: null,
    freshness: null,
    condition: null,
    contents: [],
    ...state,
  };
}

export function exampleInventory(intl: IntlShape): ItemView[] {
  return [
    item(
      "example-roll",
      "food",
      intl.formatMessage({ id: "grow.roleplay.intro.example.rollName", defaultMessage: "Filled roll" }),
      intl.formatMessage({
        id: "grow.roleplay.intro.example.rollDescription",
        defaultMessage: "Lay forgotten in your bag for too long.",
      }),
      { freshness: "spoiled" },
    ),
    item(
      "example-phone",
      "device",
      intl.formatMessage({ id: "grow.roleplay.intro.example.phoneName", defaultMessage: "Smartphone" }),
      intl.formatMessage({
        id: "grow.roleplay.intro.example.phoneDescription",
        defaultMessage: "Every use drains the battery. Wherever there is power, you can charge it again.",
      }),
      { charge: 1, capacity: 5 },
    ),
    item(
      "example-plaster",
      "other",
      intl.formatMessage({ id: "grow.roleplay.intro.example.plasterName", defaultMessage: "Plaster" }),
      intl.formatMessage({ id: "grow.roleplay.intro.example.plasterDescription", defaultMessage: "Sticks only once." }),
      { usesLeft: 1 },
    ),
    item(
      "example-knife",
      "tool",
      intl.formatMessage({ id: "grow.roleplay.intro.example.knifeName", defaultMessage: "Pocket knife" }),
      intl.formatMessage({
        id: "grow.roleplay.intro.example.knifeDescription",
        defaultMessage: "Can be used again and again, but rusts over time and goes dull with use.",
      }),
      { condition: "worn" },
    ),
  ];
}

// The characters step's cast: the two ways the panel can know someone. One the player has met and learnt a little
// about (name, shown mood, a few facts, part of what she carries), one only heard of, known by a description alone.
export function exampleCast(intl: IntlShape): CharacterView[] {
  const say = (id: string, defaultMessage: string) => text(intl.formatMessage({ id, defaultMessage }));
  return [
    {
      id: "example-met",
      placeholder: say("grow.roleplay.intro.example.metPlaceholder", "the woman in the aisle seat"),
      presence: "met",
      available: true,
      shownMood: say("grow.roleplay.intro.example.metMood", "friendly"),
      facts: [
        { kind: "name", value: say("grow.roleplay.intro.example.metName", "Lena") },
        { kind: "age", value: say("grow.roleplay.intro.example.metAge", "about 30") },
        { kind: "profession", value: say("grow.roleplay.intro.example.metProfession", "nurse") },
        { kind: "relationship", value: say("grow.roleplay.intro.example.metRelationship", "well disposed") },
      ],
      items: { known: [{ id: "example-bottle", name: say("grow.roleplay.intro.example.metItem", "water bottle") }], complete: false },
    },
    {
      id: "example-heard",
      placeholder: say("grow.roleplay.intro.example.heardPlaceholder", "the man from row 12"),
      presence: "heard_of",
      available: true,
      shownMood: null,
      facts: [],
      items: { known: [], complete: false },
    },
  ];
}
